import { DateTime } from "luxon";
import type { DepartmentLocation, NurseInShift } from "../../../utils/models";
import { filterNursesForCoverage, INCLUDE_TRAINEES_IN_COVERAGE } from "../../../utils/statsCalculations";
import type { TimeSlot } from "../types";
import { weekdayAbbreviation } from "../util/locations";
import { convertLocationToTimeSlot, convertNurseToTimeSlot, doTimeSlotsOverlap, isTimeSlotContained, splitTimeSlot } from "./timeSlotOps";

/**
 * Priority-ordered match kinds. Earlier kinds are exhausted across all (required, available) pairs
 * before later kinds run. Without this ordering, a partial overlap on a long required slot could
 * consume an available shift that exactly fits a different required slot in the same bucket.
 */
enum MatchKind {
	Exact = 0,
	AvailableContainsRequired = 1,
	RequiredContainsAvailable = 2,
	PartialOverlap = 3,
}

/**
 * Try to match a single (required, available) pair under the given match kind.
 *
 * Mutates the arrays on success: removes the consumed slot from each, then pushes any
 * remainder pieces back so subsequent passes can continue matching them. Returns whether
 * the pair was consumed.
 */
const tryMatchPair = (required: TimeSlot, available: TimeSlot, requireds: TimeSlot[], availables: TimeSlot[], kind: MatchKind): boolean => {
	const consume = (): boolean => {
		const ri = requireds.indexOf(required);
		const ai = availables.indexOf(available);
		if (ri === -1 || ai === -1) return false;
		requireds.splice(ri, 1);
		availables.splice(ai, 1);
		return true;
	};

	if (kind === MatchKind.Exact) {
		return required.start.equals(available.start) && required.end.equals(available.end) ? consume() : false;
	}

	if (kind === MatchKind.AvailableContainsRequired) {
		if (!isTimeSlotContained(available, required)) return false;
		if (!consume()) return false;
		const [beforePart, afterPart] = splitTimeSlot(available, required);
		if (beforePart) availables.push(beforePart);
		if (afterPart) availables.push(afterPart);
		return true;
	}

	if (kind === MatchKind.RequiredContainsAvailable) {
		if (!isTimeSlotContained(required, available)) return false;
		if (!consume()) return false;
		const [beforePart, afterPart] = splitTimeSlot(required, available);
		if (beforePart) requireds.push(beforePart);
		if (afterPart) requireds.push(afterPart);
		return true;
	}

	// MatchKind.PartialOverlap
	if (!doTimeSlotsOverlap(required, available)) return false;
	if (!consume()) return false;
	const overlapStart = required.start > available.start ? required.start : available.start;
	const overlapEnd = required.end < available.end ? required.end : available.end;
	if (required.start < overlapStart) {
		requireds.push({
			start: required.start,
			end: overlapStart,
			location: required.location,
			roles: required.roles,
			section: required.section ?? available.section,
			jobId: required.jobId,
		});
	}
	if (required.end > overlapEnd) {
		requireds.push({
			start: overlapEnd,
			end: required.end,
			location: required.location,
			roles: required.roles,
			section: required.section ?? available.section,
			jobId: required.jobId,
		});
	}
	if (available.start < overlapStart) {
		availables.push({
			start: available.start,
			end: overlapStart,
			location: available.location,
			roles: available.roles,
			section: available.section ?? required.section,
			jobId: available.jobId,
		});
	}
	if (available.end > overlapEnd) {
		availables.push({
			start: overlapEnd,
			end: available.end,
			location: available.location,
			roles: available.roles,
			section: available.section ?? required.section,
			jobId: available.jobId,
		});
	}
	return true;
};

/**
 * Exhaust all (required, available) pairs through the matcher for one match-kind pass.
 *
 * Restarts the inner loop after each successful match so the mutated arrays are re-walked from
 * the top — necessary because the remainder-push step may create new opportunities the current
 * loop position would otherwise skip.
 */
const runMatchPass = (requireds: TimeSlot[], availables: TimeSlot[], kind: MatchKind): void => {
	let progress = true;
	while (progress) {
		progress = false;
		outer: for (const required of [...requireds]) {
			for (const available of [...availables]) {
				if (tryMatchPair(required, available, requireds, availables, kind)) {
					progress = true;
					break outer;
				}
			}
		}
	}
};

/**
 * Fuse adjacent uncovered slots in the same location/role/job bucket into one slot when they're
 * short (≤ 2 hours each) and edge-touching.
 *
 * Prevents a long uncovered window from rendering as several tiny strips when the matcher
 * happened to chop it into pieces. The caller restricts this to one (location, roles, jobId)
 * bucket, but we guard explicitly so a future refactor can't silently merge across jobs.
 */
const mergeConsecutiveLocations = (locations: TimeSlot[]): void => {
	let keepMerging = true;
	while (keepMerging) {
		keepMerging = false;
		for (let i = 0; i < locations.length; i++) {
			const loc1 = locations[i];
			if (loc1.end.diff(loc1.start, "hours").hours > 2) continue;
			for (let j = 0; j < locations.length; j++) {
				if (i === j) continue;
				const loc2 = locations[j];
				if ((loc1.jobId || loc2.jobId) && loc1.jobId !== loc2.jobId) continue;
				if (loc1.end === loc2.start || loc1.start === loc2.end) {
					keepMerging = true;
					for (const idx of [i, j].sort((a, b) => b - a)) locations.splice(idx, 1);
					locations.push({
						start: DateTime.min(loc1.start, loc2.start),
						end: DateTime.max(loc1.end, loc2.end),
						location: loc1.location,
						roles: loc1.roles,
						section: loc1.section ?? loc2.section,
						jobId: loc1.jobId,
					});
					break;
				}
			}
			if (keepMerging) break;
		}
	}
};

/**
 * Find uncovered required-coverage windows — the "gap" slots inserted into the printout when
 * `printGaps` is on. Also used outside the printout (DayViewGaps page) to drive the on-screen
 * gap visualization, so the **positional signature is preserved** for API stability.
 *
 * Algorithm:
 * 0. Drop shifts that cannot fill a slot at all — trainees and pending-delete — via the shared
 *    `filterNursesForCoverage`, so this answers the coverage question the same way the Weekly Gap
 *    View and the staff table do
 * 1. Bucket department locations by `(location_name, roles, jobId)` — locations with the same key
 *    share an eligibility filter
 * 2. For each bucket, list every covering shift that satisfies the bucket's filter as `available`
 *    slots; expand each location config into `amountRequired` copies as `required` slots
 * 3. Run four priority match passes: Exact → AvailableContainsRequired → RequiredContainsAvailable
 *    → PartialOverlap. Each pass exhausts before the next begins.
 * 4. Fuse short adjacent leftover slots via `mergeConsecutiveLocations`
 * 5. Drop slots shorter than 2 hours (too small to be meaningful gaps)
 */
export const findLocationsWithNoNurse = (
	departmentLocations: DepartmentLocation[],
	nurseShifts: NurseInShift[],
	date: DateTime,
	timezone: string,
	dayStart: string,
): TimeSlot[] => {
	const weekdayAbbr = weekdayAbbreviation(date);

	// Whether a body counts toward a required slot is one question with one answer, shared with the
	// Weekly Gap View, the staff table and the staffing-target check via `filterNursesForCoverage`.
	// Applied once here rather than inside the per-bucket filter so no future bucket rule can skip it.
	const coveringShifts = nurseShifts.filter((shift) => filterNursesForCoverage(shift, INCLUDE_TRAINEES_IN_COVERAGE));

	const locationsByKey = new Map<string, DepartmentLocation[]>();
	departmentLocations
		.filter((loc) => loc.location !== "" && loc.days.includes(weekdayAbbr))
		.map<DepartmentLocation>((loc) => ({ ...loc, location: loc.location.trim() }))
		.forEach((loc) => {
			const key = `${loc.location}_${loc.roles}_${loc.jobId ?? ""}`;
			let bucket = locationsByKey.get(key);
			if (!bucket) {
				bucket = [];
				locationsByKey.set(key, bucket);
			}
			bucket.push(loc);
		});

	let allGaps: TimeSlot[] = [];

	for (const [, bucketLocations] of locationsByKey) {
		// Sort bucket configs to match legacy iteration order — the priority-pass matcher is
		// order-sensitive, so different orderings of required slots produce different gap shapes.
		// Primary: shift_start ASC (earliest required first).
		// Tie-break: shift_end DESC. For configs sharing shift_start (e.g. Unit 5 - CNA has both
		// 18:00-22:00 and 18:00-06:00), iterating the LONGER-ending slot first lets a fully-
		// containing nurse (e.g. Christina 16:52→06:07 Thu) consume the longer slot via Pass 2,
		// forcing the shorter overlapping slot to partial-overlap with a shorter nurse. This
		// matches legacy's `locationComparator` effective behavior — its last tie-break
		// (`a.location.localeCompare(b.shift_end)`) returns a non-zero value regardless of swap,
		// and V8's sort happened to land on shift_end-DESC for these cases.
		bucketLocations.sort((a, b) => a.shift_start.localeCompare(b.shift_start) || b.shift_end.localeCompare(a.shift_end));

		const { location, roles, jobId } = bucketLocations[0];

		const eligibleNurses = coveringShifts.filter((shift) => {
			if (!shift.location || shift.location.trim() !== location) return false;
			// When the bucket has a jobId, the job is a strict gate — otherwise an RN with jobId="nurse-job"
			// would silently satisfy an aide slot if both happen to share roles=["all"].
			if (jobId) return shift.jobId === jobId;
			return roles.length === 0 || roles.includes("all") || roles.includes(shift.role);
		});
		const availableShifts: TimeSlot[] = eligibleNurses.map((n) => convertNurseToTimeSlot(n, timezone));

		// Pool every required slot across all configs in this bucket so the priority passes can
		// pick globally-best (required, available) pairs, not greedily per-config.
		const requiredShifts: TimeSlot[] = [];
		for (const loc of bucketLocations) {
			const slot = convertLocationToTimeSlot(loc, date, timezone, dayStart);
			for (let i = 0; i < loc.amountRequired; i++) {
				requiredShifts.push({ ...slot });
			}
		}

		runMatchPass(requiredShifts, availableShifts, MatchKind.Exact);
		runMatchPass(requiredShifts, availableShifts, MatchKind.AvailableContainsRequired);
		runMatchPass(requiredShifts, availableShifts, MatchKind.RequiredContainsAvailable);
		runMatchPass(requiredShifts, availableShifts, MatchKind.PartialOverlap);

		mergeConsecutiveLocations(requiredShifts);

		allGaps = [...allGaps, ...requiredShifts.filter((slot) => slot.end.diff(slot.start, "hours").hours >= 2)];
	}

	return allGaps;
};
